/* eslint-disable react/no-unescaped-entities */
import { createContext, useCallback, useContext, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Sparkles, X, CheckCircle2, Loader2, BellRing } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/** PremiumSoonModal — luxury "Coming Soon" modal.
 *  Use via the global helper:
 *
 *     const { open } = usePremiumSoon();
 *     <button onClick={() => open()}>Passer Premium</button>
 *
 *  The modal renders a description, a waitlist input (pre-filled if logged-in),
 *  with an elegant entrance animation and a blurred backdrop.
 */
const PremiumSoonCtx = createContext({ open: () => {}, close: () => {} });

export function PremiumSoonProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  return (
    <PremiumSoonCtx.Provider value={{ open, close }}>
      {children}
      <PremiumSoonModal open={isOpen} onClose={close} />
    </PremiumSoonCtx.Provider>
  );
}

export function usePremiumSoon() {
  return useContext(PremiumSoonCtx);
}

function PremiumSoonModal({ open, onClose }) {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/premium/waitlist", {
        email: email.trim(),
        user_id: user?.user_id || null,
        role_hint: user?.role || null,
      });
      setDone(true);
      toast.success("Vous êtes sur la liste");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Inscription impossible");
    } finally {
      setSubmitting(false);
    }
  };

  // Reset state when the modal closes
  const handleClose = () => {
    onClose();
    setTimeout(() => { setDone(false); }, 200);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 sm:px-6"
          data-testid="premium-soon-modal"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-[#0E0B08]/55 backdrop-blur-md"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-md rounded-[28px] overflow-hidden bg-[#0E0B08] text-white shadow-[0_30px_80px_-20px_rgba(110,75,42,0.6)]"
            data-testid="premium-soon-modal-card"
          >
            {/* Decorative glow */}
            <div className="absolute -top-32 -right-24 w-[320px] h-[320px] rounded-full bg-[#C68E59]/35 blur-[120px] pointer-events-none" />
            <div className="absolute -bottom-24 -left-20 w-[260px] h-[260px] rounded-full bg-[#A26F3E]/30 blur-[100px] pointer-events-none" />

            <button
              type="button"
              onClick={handleClose}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              aria-label="Fermer"
              data-testid="premium-soon-close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="relative px-7 sm:px-9 py-9 sm:py-11">
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C68E59]/15 border border-[#C68E59]/35 text-[10px] uppercase tracking-[0.22em] text-[#E6C29A]" data-testid="premium-soon-badge">
                <Sparkles className="w-3 h-3" /> Coming Soon
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl mt-5 leading-[1.08] tracking-tight">
                Helencia Premium <span className="italic text-[#E6C29A]">arrive bientôt</span>
              </h2>
              <p className="mt-4 text-sm sm:text-[15px] text-white/75 leading-relaxed">
                Helencia Premium est actuellement en préparation. L'accès sera bientôt disponible.
              </p>

              {!done ? (
                <form onSubmit={submit} className="mt-7 space-y-3" data-testid="premium-soon-form">
                  <label className="block text-[10px] uppercase tracking-[0.18em] text-[#C68E59]">Email</label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@example.com"
                    className="w-full rounded-2xl bg-white/8 border border-white/15 focus:border-[#C68E59] focus:outline-none px-4 py-3 text-sm text-white placeholder:text-white/40 transition-colors"
                    data-testid="premium-soon-email"
                  />
                  <button
                    type="submit"
                    disabled={submitting || !email.trim()}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#C68E59] to-[#A26F3E] hover:from-[#D49F6A] hover:to-[#B57F4A] text-white text-sm font-semibold tracking-wide h-12 transition-all shadow-[0_10px_28px_-8px_rgba(198,142,89,0.6)] disabled:opacity-60"
                    data-testid="premium-soon-submit"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <BellRing className="w-4 h-4" />}
                    Être informé du lancement
                  </button>
                  <p className="text-[10px] text-white/40 text-center">Pas de spam. Un seul email à l'ouverture du Premium.</p>
                </form>
              ) : (
                <div className="mt-7 rounded-2xl bg-white/8 border border-[#C68E59]/35 p-5 flex items-start gap-3" data-testid="premium-soon-success">
                  <CheckCircle2 className="w-5 h-5 text-[#E6C29A] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-white">Vous êtes sur la liste</p>
                    <p className="text-xs text-white/65 mt-1">Nous vous écrirons en avant-première dès l'ouverture du Premium.</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** PremiumSoonHero — drop-in luxurious "Premium Coming Soon" hero card.
 *  Use inside the existing PremiumPage when comingSoon is true.
 */
export function PremiumSoonHero() {
  const { open } = usePremiumSoon();
  return (
    <section
      className="relative rounded-[32px] overflow-hidden bg-[#0E0B08] text-white mb-12 group"
      data-testid="premium-soon-hero"
    >
      {/* Decorative gradients */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#C68E59]/30 blur-[140px]" />
        <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#A26F3E]/25 blur-[120px]" />
      </div>

      {/* Animated halo */}
      <motion.div
        aria-hidden="true"
        className="absolute top-1/2 left-1/2 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ x: "-50%", y: "-50%", background: "radial-gradient(circle, rgba(230,194,154,0.16) 0%, transparent 60%)" }}
        animate={{ opacity: [0.5, 0.9, 0.5] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative px-6 sm:px-12 lg:px-16 py-14 sm:py-20 lg:py-24">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 backdrop-blur-md border border-white/15 text-[10px] uppercase tracking-[0.22em] text-[#E6C29A]" data-testid="premium-soon-badge-hero">
          <Lock className="w-3 h-3" /> Coming Soon
        </div>

        <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight max-w-4xl">
          Helencia Premium <span className="italic text-[#E6C29A]">arrive bientôt</span>
        </h1>
        <p className="text-base sm:text-xl mt-5 text-white/75 max-w-2xl leading-relaxed">
          Nous préparons actuellement une expérience Premium exclusive avec des collaborations avancées, des concours privés, des événements, des avantages partenaires et des fonctionnalités réservées aux membres Premium.
        </p>
        <p className="text-xs uppercase tracking-[0.22em] mt-6 text-[#C68E59]">Disponible prochainement</p>

        <div className="mt-10 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={open}
            disabled
            aria-disabled="true"
            className="inline-flex items-center gap-2 rounded-full px-7 h-12 text-sm font-semibold tracking-wide text-white bg-gradient-to-r from-[#C68E59]/50 to-[#A26F3E]/50 border border-[#C68E59]/40 cursor-not-allowed select-none shadow-[0_0_30px_-6px_rgba(198,142,89,0.5)] transition-all hover:from-[#C68E59]/65 hover:to-[#A26F3E]/65 hover:shadow-[0_0_40px_-4px_rgba(198,142,89,0.7)]"
            data-testid="premium-soon-cta-disabled"
            onClickCapture={(e) => { e.preventDefault(); open(); }}
            style={{ pointerEvents: "auto" }}
          >
            <Lock className="w-3.5 h-3.5" /> Bientôt disponible
          </button>
          <button
            type="button"
            onClick={open}
            className="inline-flex items-center gap-2 rounded-full px-6 h-12 text-sm font-medium tracking-wide bg-white/8 hover:bg-white/14 border border-white/15 text-white transition-colors"
            data-testid="premium-soon-cta-waitlist"
          >
            <BellRing className="w-3.5 h-3.5" /> Être informé du lancement
          </button>
        </div>
      </div>
    </section>
  );
}
